/**
 * ForbiddenPage — 403 Access Denied
 *
 * Shown when a user tries to access a route their current role cannot reach.
 * Provides clear feedback + quick actions to switch profile or go back.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useRBAC } from '../contexts/RBACContext';
import { ShieldOff, ArrowLeft, Users } from 'lucide-react';

const BRAND_GREEN = '#064e3b';

const ForbiddenPage = ({ rolesAllowed = [] }) => {
  const navigate = useNavigate();
  const { activeRole, roles: userRoles } = useRBAC();

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f8fafc' }}>
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header banner */}
          <div
            className="h-2"
            style={{ background: `linear-gradient(90deg, ${BRAND_GREEN}, #b48c2c)` }}
          />

          <div className="p-8 text-center">
            {/* Icon */}
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: '#fef2f2' }}
            >
              <ShieldOff className="w-8 h-8 text-red-500" />
            </div>

            {/* Title */}
            <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
              Acesso Não Autorizado
            </h1>

            {/* Description */}
            <p className="text-gray-500 mb-1">
              Seu perfil atual (<span className="font-semibold text-gray-700">{activeRole || '—'}</span>) não tem permissão para esta página.
            </p>
            <p className="text-sm text-gray-400 mb-6">
              Troque de perfil ou volte para a página anterior.
            </p>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-bold text-white transition-colors"
                style={{ backgroundColor: BRAND_GREEN }}
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </button>

              <button
                onClick={() => navigate('/central')}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <Users className="w-4 h-4" />
                Ir para Central
              </button>
            </div>

            {/* Dev diagnostics */}
            {process.env.NODE_ENV === 'development' && (
              <div className="mt-6 text-left bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Debug (dev only)</p>
                <p className="text-xs text-gray-500 font-mono">path: {window.location.pathname}</p>
                <p className="text-xs text-gray-500 font-mono">activeRole: <span className="text-amber-600">{activeRole || '—'}</span></p>
                <p className="text-xs text-gray-500 font-mono">userRoles (token): <span className="text-green-700">{JSON.stringify(userRoles)}</span></p>
                <p className="text-xs text-gray-500 font-mono">rolesAllowed: <span className="text-red-600">{JSON.stringify(rolesAllowed)}</span></p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForbiddenPage;
